import {SelectedQuoteDetails} from 'Quoting/components/Opportunity/QuotesTab';
import {CopyQuoteDetails, QuoteDeclineDetails} from 'Quoting/ducks/quoteActions';
import {MasterPolicyQuote} from 'Quoting/models/DTOs/quote';
import {getISODateString} from 'Quoting/utils/dateUtils';
import axios from 'Shared/utilities/axiosInstance';
import {getAxiosConfig, handleResponse} from 'Shared/utilities/fetchHelpers';
import {getAPIUrl} from './config';
import {ProposalResponseDTO} from 'Quoting/models/DTOs/Proposal';
import {
  DecisionHeapedTable,
  DecisionLevelTable,
  EvaluateHeapedPayload,
  EvaluateLevelPayload,
} from 'Quoting/models/DTOs/CompensationPayload';
import { getOpportunityURI } from 'Quoting/utils/utilities';

const getAxiosConfigAndHeaders = () => {
  const axiosConfig = getAxiosConfig();

  return {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
    },
  };
};

export default {
  prepareForProposal: async (quote: SelectedQuoteDetails) => {
    const endpoint = `${quote.productModel}/quotes/${quote.rootId}/${quote.revisionNo}/prepare-for-proposal`;
    const response = await axios.post<MasterPolicyQuote>(getAPIUrl(endpoint), undefined, getAxiosConfigAndHeaders());
    return handleResponse(response);
  },

  createProposal: async (
    opportunityRootId: string,
    quotes: SelectedQuoteDetails[],
    newProposalData: {
      name: string;
      proposalExpirationDate: string;
    }
  ) => {
    const endpoint = `proposals`;

    const createProposalPayload = {
      name: newProposalData.name,
      opportunity: {
        _uri: getOpportunityURI(opportunityRootId),
      },
      proposalExpirationDate: getISODateString(newProposalData.proposalExpirationDate),
      quotes: quotes.map(quote => ({
        _uri: `gentity://PolicySummary/${quote.productModel}/quote/${quote.rootId}/${quote.revisionNo}`,
      })),
    };

    const response = await axios.post<ProposalResponseDTO>(
      getAPIUrl(endpoint),
      createProposalPayload,
      getAxiosConfigAndHeaders()
    );
    return handleResponse(response);
  },

  declineQuote: async (quote: SelectedQuoteDetails, quoteDeclineDetails: QuoteDeclineDetails) => {
    const {productModel, rootId, revisionNo} = quote;
    const {declineBy, requestPayload} = quoteDeclineDetails;

    const endpoint = `${productModel}/quotes/${rootId}/${revisionNo}/decline-by-${declineBy}`;
    const response = await axios.post<MasterPolicyQuote>(
      getAPIUrl(endpoint),
      requestPayload,
      getAxiosConfigAndHeaders()
    );
    return handleResponse(response);
  },

  generateProposal: async (proposalRootId: string, proposalRevisionNo: number) => {
    const endpoint = `/proposals/${proposalRootId}/${proposalRevisionNo}/generate`;
    const response = await axios.post<ProposalResponseDTO>(getAPIUrl(endpoint), undefined, getAxiosConfigAndHeaders());
    return handleResponse(response);
  },

  evaluateDecisionTable: async (
    requestLevelPayload: EvaluateLevelPayload,
    requestHeapedPayload: EvaluateHeapedPayload,
    productCd: string
  ) => {
    const commissionLevelTable = 'Commissions Max Level';
    const commissionHeapedTable = 'Heaped Commissions Max Level';

    const responseLevel = await axios.post<DecisionHeapedTable[] | DecisionLevelTable[]>(
      getAPIUrl(`${productCd}/decisions/tables/${encodeURIComponent(commissionLevelTable)}/evaluate`),
      requestLevelPayload,
      getAxiosConfigAndHeaders()
    );

    const responseHeaped = await axios.post<DecisionHeapedTable[] | DecisionLevelTable[]>(
      getAPIUrl(`${productCd}/decisions/tables/${encodeURIComponent(commissionHeapedTable)}/evaluate`),
      requestHeapedPayload,
      getAxiosConfigAndHeaders()
    );
    return [handleResponse(responseLevel), handleResponse(responseHeaped)];
  },

  copyQuote: async (quote: SelectedQuoteDetails, copyQuoteDetails: CopyQuoteDetails) => {
    const {productModel, rootId, revisionNo} = quote;
    const {requestPayload} = copyQuoteDetails;

    const endpoint = `${productModel}/quotes/${rootId}/${revisionNo}/copy`;
    const response = await axios.post<any>(getAPIUrl(endpoint), requestPayload, getAxiosConfigAndHeaders());
    return handleResponse(response);
  },
};
